import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { AppBar, Badge, Button, Menu, MenuItem, Typography, Grid, IconButton, Autocomplete, TextField, Avatar, Divider, ListItemIcon } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications"; import { ArrowDropDown, Assignment, HelpOutline, Logout, Settings } from '@mui/icons-material';
import AccountCircle from "@mui/icons-material/AccountCircle";
import SearchIcon from "@mui/icons-material/Search";
import { getSubdepartment } from "../api/subDepartment-api";
import { useQueryClient } from "@tanstack/react-query";
import { openRightDrawer } from "../store/slices/rightDrawerSlice";
import { RIGHT_DRAWER_TYPES } from "../utils/globalConstantUtil";
import { getAllDepartments } from "../api/department-api";
import { setdepartmentData } from "../store/slices/department";

function Header() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const userRole = useSelector((state) => state?.users?.role || null);
  const { noOfNotifications } = useSelector((state) => state.header);
  const user = useSelector(state => state.users);
  const [searchQuery, setSearchQuery] = useState("");
  const [departmentsWithSubdepartments, setDepartmentsWithSubdepartments] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const fetchDepartmentsAndSubdepartments = async () => {
      try {
        const departments = await getAllDepartments();

        const departmentsWithSubs = await Promise.all(
          departments.map(async (department) => {
            const subdepartments = await getSubdepartment(department.id);
            return {
              ...department,
              subdepartments: Array.isArray(subdepartments) ? subdepartments : [],
            };
          })
        );

        setDepartmentsWithSubdepartments(departmentsWithSubs);
      } catch (error) {
        console.error("Error fetching departments and subdepartments:", error);
      }
    };
    fetchDepartmentsAndSubdepartments();
  }, []);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProjectClose = () => {
    setAnchorEl(null);
  };
  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    navigate("/app/settings-profile");
    handleProfileMenuClose();
  };
  const handleProjectClick = () => {
    navigate("/app/project-management");
    handleProjectClose();
  };

  const openNotification = () => {
    dispatch(
      openRightDrawer({
        header: "Notifications",
        bodyType: RIGHT_DRAWER_TYPES.NOTIFICATION,
      })
    );
  };

  const logoutUser = () => {
    localStorage.clear();
    navigate("/login");
  };

  const handleSearch = (e, value) => {
    setSearchQuery(value);
  };

  const handleMainClick = (department) => {
    dispatch(setdepartmentData(department));
    navigate("/app/Subdepartments");
    queryClient.invalidateQueries(["subDepartments"]);
  };

  const filteredOptions = departmentsWithSubdepartments
    .flatMap((department) => [
      {
        type: "department",
        id: department.id,
        name: department.name,
        description: department.description,
      },
      ...department.subdepartments.map((sub) => ({
        type: "subdepartment",
        departmentId: department.id,
        name: sub.name,
      })),
    ])
    .filter((option) => option.name?.toLowerCase().includes(searchQuery?.toLowerCase()));

  const getLastPathSegment = (path) => {
    const segments = path.split('/');
    return segments[segments.length - 1] || 'Home';
  };

  const lastPathSegment = getLastPathSegment(location.pathname);

  return (
    <AppBar
      sx={{
        backgroundImage: "linear-gradient(to left, #4E65FF, #92EFFD)",
        padding: "0.5vh 1vw",
      }}
    >
      <Grid container justifyContent="space-between" alignItems="center">
        {/* Logo */}
        <Grid item xs={4} md={2}>
          <Link to="/app/welcome">
            <Avatar
              variant="square"
              src="/logo.png"
              alt="Logo"
              sx={{ width: "4.5vw", height: "auto", marginRight: "0.5vw" }}
            />
          </Link>
        </Grid>
        <Grid item xs={4} md={2}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              background: "-webkit-radial-gradient(circle, rgba(28,87,181,1) 0%, rgba(9,73,89,0.9864320728291317) 57%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
            }}
          >
            Research Hub
          </Typography>
        </Grid>
        <Grid item xs={4} md={2}>
          <Typography
            variant="h6"
            sx={{
              fontSize: "1.2rem",
              fontWeight: "bold",
              background: "-webkit-radial-gradient(circle, rgba(28,87,181,1) 0%, rgba(9,73,89,0.9864320728291317) 57%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
            }}
          >
            {lastPathSegment}
          </Typography>
        </Grid>

        {/* Search, Notifications, Profile/Login */}
        <Grid item xs={12} md={6} sx={{ textAlign: "right" }}>
          <Grid container justifyContent="flex-end" alignItems="center" spacing={2}>
            {/* Search Input */}
            <Grid item xs={12} md={8}>
              <Autocomplete
                freeSolo
                options={filteredOptions}
                getOptionLabel={(option) => option.name}
                sx={{
                  padding: "0 !important",
                }}

                renderOption={(props, option) => (
                  <MenuItem
                    {...props}
                    onClick={() => {
                      if (option.type === "department") {
                        handleMainClick(option);
                      }
                    }}
                    sx={{
                      fontSize: '0.8vw', lineHeight: "150% !important",
                      fontWeight: option.type === "department" ? "bold" : "normal",
                      padding: "0.5vw"
                    }}

                  >
                    {option.name}
                  </MenuItem>
                )}
                onInputChange={handleSearch}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <IconButton >
                            <SearchIcon sx={{ color: "white", fontSize: '1vw' }} />
                          </IconButton>
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{
                      mt: { md: "0.7vh", xl: 0 },
                      display: { md: "block", xs: "none" },
                      "& .MuiOutlinedInput-root": {
                        padding: { md: "0.4vw !important", xs: "10px 10px" },
                        height: { xl: "4vh", lg: "4vh", md: "6vh", xs: "8vh" },
                        width: "20vw",
                        color: "white !important",
                        "& fieldset": {
                          borderBottom: {
                            md: "0.15vw solid white",
                            xs: "2px solid white",
                          },
                          borderTop: "0.15vw solid transparent",
                          borderLeft: "0.15vw solid transparent",
                          borderRight: "0.15vw solid transparent",
                          padding: { md: "0.4vw !important", xs: "16px 16px" },
                        },
                        "&:hover fieldset": {
                          borderBottom: {
                            md: "0.15vw solid white",
                            xs: "2px solid white",
                          },
                          borderTop: "0.15vw solid transparent",
                          borderLeft: "0.15vw solid transparent",
                          borderRight: "0.15vw solid transparent",
                        },
                        "&.Mui-focused fieldset": {
                          border: {
                            md: "0.15vw solid white",
                            xs: "2px solid white",
                          },
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "white",
                        fontSize: "0.9vw",
                        fontWeight: "bold",
                      },
                    }}
                  />
                )}
              />
            </Grid>
            {/* Notifications */}
            {userRole && (userRole === "admin" || userRole === "faculty") && (
              <Grid item>
                <IconButton onClick={openNotification}>
                  <Badge
                    badgeContent={noOfNotifications}
                    color="secondary"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "#4E65FF",
                        color: "white",
                      },
                    }}
                  >
                    <NotificationsIcon sx={{ color: "white" }} />
                  </Badge>
                </IconButton>
              </Grid>
            )}

            {/* Profile/Logout */}
            {userRole ? (
              <Grid item>
                <IconButton onClick={handleProfileMenuOpen} style={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    sx={{
                      height: 50, width: 50, position: 'absolute',
                      border: '5px solid white', backgroundColor: '#699BFE', fontSize: '2rem'
                    }}
                    src={user?.profileImage || '/Banner/defaultuser.jpeg'}
                  >
                    {user?.name?.charAt(0)}
                  </Avatar>
                  <ArrowDropDown />
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleProfileMenuClose}
                  PaperProps={{
                    sx: {
                      maxHeight: 280, 
                      width: '240px', 
                      borderRadius: '0 0 8px 8px', 
                      boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                      mt: "2vh",
                    },
                  }}
                >
                  <MenuItem disabled style={{ fontWeight: 'bold', fontSize: '16px', color: '#555' }}>
                    <AccountCircle sx={{ mr: 2 }} />
                    {user?.name || 'User Name'}
                  </MenuItem>
                  <Divider sx={{ my: 1 }} />
                  <MenuItem onClick={handleProfileClick} sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      <Settings fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="body2">Account Settings</Typography>
                  </MenuItem>
                  <MenuItem onClick={handleProjectClick} sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      <Assignment fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="body2">Manage Projects</Typography>
                  </MenuItem>
                  <MenuItem sx={{ py: 1.5 }}>
                    <ListItemIcon>
                      <HelpOutline fontSize="small" />
                    </ListItemIcon>
                    <Typography variant="body2">Help Center</Typography>
                  </MenuItem>
                  <Divider sx={{ my: 1 }} />
                  <MenuItem onClick={logoutUser} sx={{ py: 1.5, color: 'red' }}>
                    <ListItemIcon>
                      <Logout fontSize="small" sx={{ color: 'red' }} />
                    </ListItemIcon>
                    <Typography variant="body2">Logout</Typography>
                  </MenuItem>
                </Menu>

              </Grid>

            ) : (
              <>
                {/* Login */}
                <Grid item>
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => navigate("/login")}
                    sx={{ color: "white", borderColor: "white" }}
                  >
                    Login
                  </Button>
                </Grid>
                {/* Register */}
                <Grid item>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/register")}
                    sx={{
                      backgroundColor: "#4E65FF",
                      color: "white",
                      "&:hover": { backgroundColor: "#3a4ec9" },
                    }}
                  >
                    Register
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
    </AppBar>
  );
}

export default Header;
